export const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000';
export const SYSTEM_USER_NAME = 'System';

export enum UserRole {
  Admin = 'admin',
  HR = 'hr',
  Accounting = 'accounting',
  Director = 'director',
  Manager = 'manager',
  Member = 'member',
  Vendor = 'vendor',
  SystemSupport = 'system_support',
  Production = 'production',
}

export const UserRoleLabel: Record<UserRole, string> = {
  [UserRole.Admin]: 'Admin',
  [UserRole.HR]: 'HR',
  [UserRole.Accounting]: 'Accounting',
  [UserRole.Director]: 'Director',
  [UserRole.Manager]: 'Manager',
  [UserRole.Member]: 'Member',
  [UserRole.Vendor]: 'Vendor',
  [UserRole.SystemSupport]: 'System Support',
  [UserRole.Production]: 'Production',
}

export enum ProjectType {
  OneTime = 'one_time',
  Recurring = 'recurring',
}

/**
 * Project "type" attribute on the child `projects` entity (MT-1257).
 * Distinct from {@link ProjectType} (which lives on `project_groups`).
 * Mirrors the `project_category` Postgres enum. `Event` projects carry an
 * event date range (`event_start_date` / `event_end_date`).
 */
export enum ProjectCategory {
  General = 'general',
  Event = 'event',
  ProgramOneTime = 'program_one_time',
  ProgramRecurring = 'program_recurring',
  Product = 'product',
}

export const PROJECT_CATEGORY_LABELS: Record<ProjectCategory, string> = {
  [ProjectCategory.General]: 'General',
  [ProjectCategory.Event]: 'Event',
  [ProjectCategory.ProgramOneTime]: 'Program - One Time',
  [ProjectCategory.ProgramRecurring]: 'Program - Recurring',
  [ProjectCategory.Product]: 'Product',
};

/**
 * Query engine of a data source the AI query assistant can target. Mirrors the
 * `engine` field of DB_CONNECTIONS entries and the `data_dictionaries.engine`
 * column (migration 175). Postgres covers both the default Merkos DB and named
 * cross-region Postgres sources (e.g. met-at-chabad); Athena is the finance data
 * warehouse; Neo4j is the sales graph.
 */
export enum DbEngine {
  Postgres = 'postgres',
  Athena = 'athena',
  Neo4j = 'neo4j',
}

export const DbEngineLabel: Record<DbEngine, string> = {
  [DbEngine.Postgres]: 'PostgreSQL',
  [DbEngine.Athena]: 'Athena',
  [DbEngine.Neo4j]: 'Neo4j',
};

/**
 * Engine recorded on each row of the query_logs SQL-execution log (migration
 * 118). Mirrors the query_logs.engine CHECK constraint exactly. Distinct from
 * DbEngine because the log separates the default/named Merkos Postgres
 * ('postgres', logged automatically by the pg pool) from cross-region remote
 * Postgres sources such as met-at-chabad ('remote-pg', run via a bridge Lambda).
 */
export enum QueryLogEngine {
  Postgres = 'postgres',
  Athena = 'athena',
  Neo4j = 'neo4j',
  RemotePg = 'remote-pg',
}

export const QueryLogEngineLabel: Record<QueryLogEngine, string> = {
  [QueryLogEngine.Postgres]: 'PostgreSQL',
  [QueryLogEngine.Athena]: 'Athena',
  [QueryLogEngine.Neo4j]: 'Neo4j',
  [QueryLogEngine.RemotePg]: 'Remote PG',
};

/**
 * State of a data-dictionary discovery run (data_dictionaries.status, migration
 * 176). Discovery runs asynchronously, so a source is 'running' while a job is in
 * flight, 'ready' once persisted, and 'error' if the job failed.
 */
export enum DiscoveryStatus {
  Running = 'running',
  Ready = 'ready',
  Error = 'error',
}

/**
 * External shop/store system a department's sales are matched against.
 * A department can map to multiple shops (see Department.shop_mappings).
 */
export enum ShopType {
  Neo4j = 'neo4j',
  Shopify = 'shopify',
}

export const ShopTypeLabel: Record<ShopType, string> = {
  [ShopType.Neo4j]: 'Neo4j',
  [ShopType.Shopify]: 'Shopify',
};

/**
 * Shopify order financial status (orders_header.displayfinancialstatus /
 * v_orders.payment in the Athena `shopify` schema). Mirrors the distinct values
 * present in Athena. Backs the Shopify payments list filter and the paid-income
 * total on the finance report.
 */
export enum ShopifyFinancialStatus {
  Paid = 'PAID',
  Pending = 'PENDING',
  Refunded = 'REFUNDED',
  PartiallyRefunded = 'PARTIALLY_REFUNDED',
  PartiallyPaid = 'PARTIALLY_PAID',
}

export const ShopifyFinancialStatusLabel: Record<ShopifyFinancialStatus, string> = {
  [ShopifyFinancialStatus.Paid]: 'Paid',
  [ShopifyFinancialStatus.Pending]: 'Pending',
  [ShopifyFinancialStatus.Refunded]: 'Refunded',
  [ShopifyFinancialStatus.PartiallyRefunded]: 'Partially Refunded',
  [ShopifyFinancialStatus.PartiallyPaid]: 'Partially Paid',
};

/**
 * Shopify statuses that count toward paid income on the finance report totals.
 * Kept deliberately narrow (gross of refunds) for now — adjust here once the
 * refund figures have been analysed. The payments LIST shows all statuses
 * regardless; this only gates the summed income number.
 */
export const SHOPIFY_INCOME_STATUSES: ShopifyFinancialStatus[] = [
  ShopifyFinancialStatus.Paid,
  ShopifyFinancialStatus.PartiallyPaid,
];

/**
 * Payment.status values in the Neo4j sales graph (external system). These are
 * the statuses that count toward sales income on the finance report and back
 * the Payments view / income drill-down.
 */
export enum PaymentGraphStatus {
  Paid = 'paid',
  Succeeded = 'succeeded',
  Refunded = 'refunded',
}

export const PaymentGraphStatusLabel: Record<PaymentGraphStatus, string> = {
  [PaymentGraphStatus.Paid]: 'Paid',
  [PaymentGraphStatus.Succeeded]: 'Succeeded',
  [PaymentGraphStatus.Refunded]: 'Refunded',
};

/** Statuses that count toward sales income (paid / succeeded / refunded). */
export const INCOME_PAYMENT_STATUSES: PaymentGraphStatus[] = [
  PaymentGraphStatus.Paid,
  PaymentGraphStatus.Succeeded,
  PaymentGraphStatus.Refunded,
];

export enum ProjectStatus {
  Draft = 'draft',
  Active = 'active',
  Completed = 'completed',
  Archived = 'archived',
}

export enum ObjectiveStatus {
  NotStarted = 'not_started',
  InProgress = 'in_progress',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

export enum MeasurementType {
  Absolute = 'absolute',
  Percentage = 'percentage',
}

/**
 * Lifecycle of an employee KPI/goals set. Mirrors the `kpi_set_status`
 * Postgres enum (migration 226).
 *   - draft      — being created/edited, not yet sent for sign-off.
 *   - submitted  — sent in an inquiry requesting sign-off; awaiting the
 *                  employee's and manager's approval.
 *   - signed_off — approved by both the employee and their manager.
 *   - cancelled  — withdrawn; no longer in effect.
 */
export enum KpiSetStatus {
  Draft = 'draft',
  Submitted = 'submitted',
  SignedOff = 'signed_off',
  Cancelled = 'cancelled',
}

/** Whether a KPI-set line is an outcome-oriented goal or a measurable KPI.
 *  Mirrors the `kpi_item_type` Postgres enum (migration 226). */
export enum KpiItemType {
  Goal = 'goal',
  Kpi = 'kpi',
}

export enum BudgetStatus {
  Draft = 'draft',
  Submitted = 'submitted',
  Approved = 'approved',
  Rejected = 'rejected',
  Expired = 'expired',
  Cancelled = 'cancelled',
}

export enum AccountType {
  Expense = 'expense',
  Income = 'income',
}

export enum GlueJobRunStatus {
  Running = 'running',
  Succeeded = 'succeeded',
  Failed = 'failed',
  Stopped = 'stopped',
}

export enum DatasetRefreshRunStatus {
  Running = 'running',
  Succeeded = 'succeeded',
  Failed = 'failed',
  Cancelled = 'cancelled',
}

export enum ExpenseStatus {
  Request = 'request',
  UnderReview = 'under_review',
  Approved = 'approved',
  Rejected = 'rejected',
  Paid = 'paid',
}

// ── Grant suggestions (MT-1662) ────────────────────────────────
// Mirrors the grant_suggestions.status / .frequency CHECK constraints (migration 258).

export enum GrantSuggestionStatus {
  Request = 'request',
  Review = 'review',
  Approved = 'approved',
  Rejected = 'rejected',
}

export const GrantSuggestionStatusLabel: Record<GrantSuggestionStatus, string> = {
  [GrantSuggestionStatus.Request]: 'Request',
  [GrantSuggestionStatus.Review]: 'Review',
  [GrantSuggestionStatus.Approved]: 'Approved',
  [GrantSuggestionStatus.Rejected]: 'Rejected',
};

/** Statuses that count against an employee's annual grant-suggestion cap. */
export const GRANT_SUGGESTION_CAPPED_STATUSES: readonly GrantSuggestionStatus[] = [
  GrantSuggestionStatus.Request,
  GrantSuggestionStatus.Review,
  GrantSuggestionStatus.Approved,
];

export enum GrantSuggestionFrequency {
  OneTime = 'one_time',
  Monthly = 'monthly',
}

export const GrantSuggestionFrequencyLabel: Record<GrantSuggestionFrequency, string> = {
  [GrantSuggestionFrequency.OneTime]: 'One time',
  [GrantSuggestionFrequency.Monthly]: 'Monthly',
};

export const ExpenseStatusLabel: Record<ExpenseStatus, string> = {
  [ExpenseStatus.Request]: 'Request',
  [ExpenseStatus.UnderReview]: 'Under Review',
  [ExpenseStatus.Approved]: 'Approved',
  [ExpenseStatus.Rejected]: 'Rejected',
  [ExpenseStatus.Paid]: 'Paid',
};

/**
 * Refund list filter (requirement 6). Not a DB column — a UI/query filter over
 * the `is_refund` boolean. Omitted = show both refunds and non-refunds.
 */
export enum RefundFilter {
  /** Only refund rows (is_refund = true). */
  Only = 'only',
  /** Exclude refund rows (is_refund = false). */
  Exclude = 'exclude',
}

export const RefundFilterLabel: Record<RefundFilter, string> = {
  [RefundFilter.Only]: 'Refunds only',
  [RefundFilter.Exclude]: 'Exclude refunds',
};

export enum PaymentMethod {
  Check = 'check',
  Transfer = 'transfer',
  PayPal = 'paypal',
  CreditCard = 'credit_card',
  /**
   * Payroll (MT-1426): a payment routed through an open payroll. Only valid when
   * the expense target_account is an employee; the expense is linked to a specific payroll
   * (see Expense.payroll_id) and its account is forced to the "Staff" account.
   * When the payroll is closed, its linked approved expenses are marked paid.
   */
  Payroll = 'payroll',
  /**
   * Paid through a Mercury remittance. The rail (ACH / wire) is not part of this
   * value — it lives on the linked remittance, so read it from there
   * (see `paymentMethodDisplay`). Enrollment sets this as the vendor default;
   * the form stays editable until a remittance is in-flight or paid.
   */
  Remittance = 'remittance',
}

export const PaymentMethodLabel: Record<PaymentMethod, string> = {
  [PaymentMethod.Check]: 'Check',
  [PaymentMethod.Transfer]: 'Bank Transfer',
  [PaymentMethod.PayPal]: 'PayPal',
  [PaymentMethod.CreditCard]: 'Credit Card',
  [PaymentMethod.Payroll]: 'Payroll',
  [PaymentMethod.Remittance]: 'ACH Direct',
}

/**
 * Methods a user may pick, and the values expense create/update accept.
 * Remittance is included. Payroll stays in the list; callers hide it unless
 * the expense is an employee.
 */
export const SELECTABLE_PAYMENT_METHODS = [
  PaymentMethod.Check,
  PaymentMethod.Transfer,
  PaymentMethod.PayPal,
  PaymentMethod.CreditCard,
  PaymentMethod.Payroll,
  PaymentMethod.Remittance,
] as const;

/**
 * Options for a method picker. Remittance is a normal choice (preferred after
 * enroll). Callers still hide Payroll unless the expense is an employee.
 */
export function paymentMethodChoices(_current?: PaymentMethod | string | null): PaymentMethod[] {
  return [...SELECTABLE_PAYMENT_METHODS];
}

/**
 * Methods a payee can move to when electronic payment is removed.
 * Payroll has to be tied to a payroll. Remittance is the method they are leaving.
 */
export const UNENROLL_REPLACEMENT_METHODS: PaymentMethod[] = [
  PaymentMethod.Check,
  PaymentMethod.Transfer,
  PaymentMethod.PayPal,
  PaymentMethod.CreditCard,
];

/** What the actor should verify. Moving the method does not fill these in. */
export function unenrollMethodCheckNote(method: PaymentMethod): string {
  switch (method) {
    case PaymentMethod.Check:
      return 'Check that each expense has a mailing address. This does not fill it in.';
    case PaymentMethod.Transfer:
      return 'Check that each expense has a mailing address and bank details. This does not fill them in.';
    case PaymentMethod.PayPal:
      return 'Check that each expense has a PayPal email. This does not fill it in.';
    case PaymentMethod.CreditCard:
      return 'Check that each expense is ready to be paid by card. This does not fill that in.';
    default:
      return 'Check the payment details on each expense. This does not fill them in.';
  }
}

/**
 * Method label for display. Expense stores remittance; ACH / wire comes
 * from the linked line. An ACH remittance renders as plain "ACH Direct"
 * (the label already names the rail); a wire renders "ACH Direct (Wire)".
 */
export function paymentMethodDisplay(
  method: PaymentMethod | string | null | undefined,
  remittanceMethod?: RemittanceMethod | string | null,
): string {
  if (!method) return '';
  const label = PaymentMethodLabel[method as PaymentMethod] ?? method;
  if (method !== PaymentMethod.Remittance) return label;
  const rail = remittanceMethod
    ? RemittanceMethodLabel[remittanceMethod as RemittanceMethod] ?? remittanceMethod
    : null;
  if (!rail || remittanceMethod === RemittanceMethod.Ach) return label;
  return `${label} (${rail})`;
}

/**
 * The exact name of the accounts row a Payroll-method expense must be booked to
 * (MT-1426). The Staff account is seeded by migration 228; the expense backend
 * resolves it by this name (case-insensitive) and forces it onto Payroll
 * expenses, so the value here must mirror the seeded account name exactly.
 */
export const STAFF_ACCOUNT_NAME = 'Staff';;

export enum EmploymentStatus {
  Active = 'active',
  OnLeave = 'on_leave',
  Terminated = 'terminated',
  Pending = 'pending',
}

/**
 * How a notification group's membership is managed. Mirrors the
 * notification_groups.managed_type CHECK constraint.
 *  - Department: members are the active users associated with the group's
 *    department (default department, or a role in the department / its
 *    project groups / its projects).
 *  - Role: members are all active users with the group's global role.
 * A group with no managed type (null) is manually curated.
 */
export enum NotificationGroupManagedType {
  Department = 'department',
  Role = 'role',
}

/** Source of a notification group membership row. */
export enum NotificationGroupMemberSource {
  /** Added by a person; preserved across auto recompute. */
  Manual = 'manual',
  /** Added by the auto-membership rule. */
  Auto = 'auto',
}

/**
 * Global roles that get an auto-managed role notification group.
 * Matches the roles called out in MT-1330 (admin, director, hr, manager, member).
 */
export const AUTO_ROLE_GROUP_ROLES: UserRole[] = [
  UserRole.Admin,
  UserRole.Director,
  UserRole.HR,
  UserRole.Manager,
  UserRole.Member,
];

export enum EmployeeType {
  FullTime = 'full_time',
  PartTime = 'part_time',
  Contractor = 'contractor',
  Temporary = 'temporary',
  Intern = 'intern',
}

export const EmployeeTypeLabel: Record<EmployeeType, string> = {
  [EmployeeType.FullTime]: 'Full Time',
  [EmployeeType.PartTime]: 'Part Time',
  [EmployeeType.Contractor]: 'Contractor',
  [EmployeeType.Temporary]: 'Temporary',
  [EmployeeType.Intern]: 'Intern',
};

/**
 * Lifecycle status of a job posting. Mirrors the `job_status` Postgres enum
 * (migrations 196_jobs.sql + 225 adds `fulfill`). Only `Open` postings are shown
 * on the public site.
 *
 * `Fulfill` marks a position that has been filled — an employee is actively
 * linked to it (see job_employee_links). It is an internal-only status and is
 * never surfaced on the public or in-app open-positions listings.
 */
export enum JobStatus {
  Draft = 'draft',
  Open = 'open',
  Fulfill = 'fulfill',
  Closed = 'closed',
  Cancelled = 'cancelled',
}

export const JobStatusLabel: Record<JobStatus, string> = {
  [JobStatus.Draft]: 'Draft',
  [JobStatus.Open]: 'Open',
  [JobStatus.Fulfill]: 'Fulfilled',
  [JobStatus.Closed]: 'Closed',
  [JobStatus.Cancelled]: 'Cancelled',
};

/**
 * Seniority level of a job/position. Mirrors the `jobs.job_level` CHECK
 * constraint (migration 225_jobs_management.sql).
 */
export enum JobLevel {
  Director = 'director',
  Manager = 'manager',
  Employee = 'employee',
}

export const JobLevelLabel: Record<JobLevel, string> = {
  [JobLevel.Director]: 'Director',
  [JobLevel.Manager]: 'Manager',
  [JobLevel.Employee]: 'Employee',
};

/**
 * Whether a job is a one-off session engagement or an ongoing role. Mirrors the
 * `jobs.session_type` CHECK constraint (migration 225_jobs_management.sql).
 */
export enum JobSessionType {
  Session = 'session',
  Ongoing = 'ongoing',
}

export const JobSessionTypeLabel: Record<JobSessionType, string> = {
  [JobSessionType.Session]: 'Session Job',
  [JobSessionType.Ongoing]: 'Ongoing',
};

/**
 * Worker classification for a job — a payroll employee or an outside
 * contractor. Mirrors the `jobs.worker_type` CHECK constraint
 * (migration 225_jobs_management.sql).
 */
export enum JobWorkerType {
  Employee = 'employee',
  Contractor = 'contractor',
}

export const JobWorkerTypeLabel: Record<JobWorkerType, string> = {
  [JobWorkerType.Employee]: 'Employee',
  [JobWorkerType.Contractor]: 'Contractor',
};

/**
 * Where an open job posting is surfaced. Mirrors the `job_visibility` Postgres
 * enum (migration 200_job_visibility.sql).
 *  - `Internal` — shown only inside the app to logged-in staff (who can apply in-app).
 *  - `External` — shown only on the public jobs site (jobs.merkos302.com).
 *  - `Both`     — shown in both places.
 * Only `open` postings are ever surfaced anywhere, regardless of visibility.
 */
export enum JobVisibility {
  Internal = 'internal',
  External = 'external',
  Both = 'both',
}

export const JobVisibilityLabel: Record<JobVisibility, string> = {
  [JobVisibility.Internal]: 'Internal only',
  [JobVisibility.External]: 'External only',
  [JobVisibility.Both]: 'Internal & External',
};

/**
 * Status of a submitted job application as it moves through review. Mirrors the
 * `job_application_status` Postgres enum (migrations 196_jobs.sql + 218 adds
 * `hiring`).
 *
 * `Hiring` is the point at which HR has chosen the candidate and kicks off the
 * onboarding flow (message the hiring manager to collect the new-employee
 * details). The candidate becomes `Hired` only once HR approves the collected
 * information and the system user is created.
 */
export enum JobApplicationStatus {
  New = 'new',
  Reviewing = 'reviewing',
  Interviewing = 'interviewing',
  Hiring = 'hiring',
  Rejected = 'rejected',
  Hired = 'hired',
}

export const JobApplicationStatusLabel: Record<JobApplicationStatus, string> = {
  [JobApplicationStatus.New]: 'New',
  [JobApplicationStatus.Reviewing]: 'Reviewing',
  [JobApplicationStatus.Interviewing]: 'Interviewing',
  [JobApplicationStatus.Hiring]: 'Hiring',
  [JobApplicationStatus.Rejected]: 'Rejected',
  [JobApplicationStatus.Hired]: 'Hired',
};

/**
 * Lifecycle of an employee-onboarding record (the new-hire handoff between HR
 * and the hiring manager). Mirrors the `onboarding_status` Postgres enum
 * (migration 218_employee_onboarding.sql).
 *
 *  - `PendingManager` — HR started onboarding; awaiting the hiring manager to
 *    fill in the candidate's details.
 *  - `Submitted`      — the hiring manager submitted the details; awaiting HR review.
 *  - `Approved`       — HR reviewed and approved; ready to create the system user.
 *  - `Completed`      — the system user/employee was created from the onboarding.
 */
export enum OnboardingStatus {
  PendingManager = 'pending_manager',
  Submitted = 'submitted',
  Approved = 'approved',
  Completed = 'completed',
}

export const OnboardingStatusLabel: Record<OnboardingStatus, string> = {
  [OnboardingStatus.PendingManager]: 'Awaiting hiring manager',
  [OnboardingStatus.Submitted]: 'Awaiting HR review',
  [OnboardingStatus.Approved]: 'Approved — ready to create user',
  [OnboardingStatus.Completed]: 'Completed',
};

/**
 * Direction of a message in an applicant correspondence thread. Mirrors the
 * `direction` CHECK constraint on `job_application_messages` (migration 199).
 * `Outbound` = staff → applicant (delivered by email). `Inbound` = a reply from
 * the applicant, logged manually by staff (no inbound email parsing yet).
 */
export enum JobApplicationMessageDirection {
  Outbound = 'outbound',
  Inbound = 'inbound',
}

export const JobApplicationMessageDirectionLabel: Record<JobApplicationMessageDirection, string> = {
  [JobApplicationMessageDirection.Outbound]: 'Sent to applicant',
  [JobApplicationMessageDirection.Inbound]: 'From applicant',
};

/**
 * How a job posting's compensation is structured. Mirrors the `job_salary_type`
 * Postgres enum (migration 206_job_salary_fields.sql). `Hourly` postings quote a
 * per-hour rate range; `Fixed` postings quote a fixed (typically annual) salary
 * range.
 */
export enum JobSalaryType {
  Hourly = 'hourly',
  Fixed = 'fixed',
}

export const JobSalaryTypeLabel: Record<JobSalaryType, string> = {
  [JobSalaryType.Hourly]: 'Hourly',
  [JobSalaryType.Fixed]: 'Fixed price',
};

/**
 * A reviewer's hiring recommendation on a candidate. Mirrors the
 * `job_review_recommendation` Postgres enum (migration 207_job_application_reviews.sql).
 */
export enum JobReviewRecommendation {
  StrongYes = 'strong_yes',
  Yes = 'yes',
  Neutral = 'neutral',
  No = 'no',
  StrongNo = 'strong_no',
}

export const JobReviewRecommendationLabel: Record<JobReviewRecommendation, string> = {
  [JobReviewRecommendation.StrongYes]: 'Strong yes',
  [JobReviewRecommendation.Yes]: 'Yes',
  [JobReviewRecommendation.Neutral]: 'Neutral',
  [JobReviewRecommendation.No]: 'No',
  [JobReviewRecommendation.StrongNo]: 'Strong no',
};

export enum NameTitle {
  Mr = 'Mr',
  Mrs = 'Mrs',
  Ms = 'Ms',
  Rabbi = 'Rabbi',
}

export enum DocumentType {
  W9 = 'w9',
  W8BEN = 'w8ben',
  NDA = 'nda',
  OfferLetter = 'offer_letter',
  IdDocument = 'id_document',
  Other = 'other',
}

export enum VendorStatus {
  Active = 'active',
  Inactive = 'inactive',
}

export const VendorStatusLabel: Record<VendorStatus, string> = {
  [VendorStatus.Active]: 'Active',
  [VendorStatus.Inactive]: 'Inactive',
};

export enum VendorDocumentType {
  W9 = 'w9',
  W8BEN = 'w8ben',
  Invoice = 'invoice',
  Receipt = 'receipt',
  Contract = 'contract',
  Other = 'other',
}

/** Actions recorded in the audit_history table */
export enum AuditAction {
  Create = 'create',
  Update = 'update',
  StatusChange = 'status_change',
  Delete = 'delete',
  Login = 'login',
  BulkCreate = 'bulk_create',
  Link = 'link',
  Unlink = 'unlink',
  Export = 'export',
  Upload = 'upload',
  /** A time-policy alert rule fired on a time entry (acknowledged or blocked). */
  Alert = 'alert',
  /** A user viewed a sensitive/financial surface (e.g. the finance report). */
  View = 'view',
}

/** Entity types used for threads, audit trail, and documents */
export enum EntityType {
  Expense = 'expense',
  Vendor = 'vendor',
  Subscription = 'subscription',
  Budget = 'budget',
  BudgetLine = 'budget_line',
  OrganizationBudget = 'organization_budget',
  FiscalYear = 'fiscal_year',
  Project = 'project',
  ProjectGroup = 'project_group',
  Department = 'department',
  /**
   * A file stored directly in the entity-linked file system under a folder
   * (custom, album, or system root) rather than under a domain entity. The
   * document's `entity_id` is the owning `folders.id`. See file-system.ts.
   */
  Folder = 'folder',
  Objective = 'objective',
  MetricEntry = 'metric_entry',
  Employee = 'employee',
  CreditCard = 'credit_card',
  CcTransaction = 'cc_transaction',
  User = 'user',
  Account = 'account',
  TimeEntry = 'time_entry',
  TargetPlan = 'target_plan',
  Lead = 'lead',
  Pledge = 'pledge',
  PledgeDistribution = 'pledge_distribution',
  PledgeReceived = 'pledge_received',
  Thread = 'thread',
  DocumentType = 'document_type',
  AdminSetting = 'admin_setting',
  SavedQuery = 'saved_query',
  MetricQuery = 'metric_query',
  EmployeeAgreement = 'employee_agreement',
  OutOfOffice = 'out_of_office',
  MeasurementEntity = 'measurement_entity',
  TimeAlertRule = 'time_alert_rule',
  LogAlertRule = 'log_alert_rule',
  TimeEntryAlert = 'time_entry_alert',
  CalendarEvent = 'calendar_event',
  TvAccessCode = 'tv_access_code',
  TvDevice = 'tv_device',
  Job = 'job',
  JobApplication = 'job_application',
  EmployeeOnboarding = 'employee_onboarding',
  Location = 'location',
  /** An employee goals/KPI set (migration 226). */
  KpiSet = 'kpi_set',
  /** The finance report surface — used to audit who viewed it (no single row id). */
  FinanceReport = 'finance_report',
  /** The admin email log surface — used as a saved-views entity type. */
  EmailLog = 'email_log',
  /** Mercury remittance destination (vendor XOR employee). */
  TargetAccount = 'target_account',
  /** One outbound payout attempt (ACH, wire, later PayPal). */
  Remittance = 'remittance',
  /** Pay-now group of remittances. */
  RemittanceBatch = 'remittance_batch',
  /** Payroll runs surface — used as a saved-views entity type. */
  Payroll = 'payroll',
  /** Salary records surface — used as a saved-views entity type. */
  Salary = 'salary',
  /** Payments surface — used as a saved-views entity type. */
  Payment = 'payment',
  /** Feedback admin surface — used as a saved-views entity type. */
  Feedback = 'feedback',
  /** Admin audit-history surface — used as a saved-views entity type. */
  AuditHistory = 'audit_history',
  /** Admin SQL-logs surface — used as a saved-views entity type. */
  SqlLog = 'sql_log',
  /** Communication center surface — used as a saved-views entity type. */
  Communication = 'communication',
  /** Office & desks surface — used as a saved-views entity type. */
  Office = 'office',
  /** An employee's suggestion that the org grant funds to an institution (MT-1662). */
  GrantSuggestion = 'grant_suggestion',
  /** A user-built custom dashboard (MT-1666). */
  Dashboard = 'dashboard',
  /** An app allowed (or blocked) to use the SQL bridge (Admin → Monitor → SQL Bridge). */
  SqlBridgeClient = 'sql_bridge_client',
}

// ── IP → Location mapping (audit geolocation) ──────────────────

/**
 * Category of a named physical location that IP addresses are mapped to. Drives
 * the icon/color shown beside a resolved IP in the audit view. Mirrors the
 * `location_category` CHECK constraint on the `locations` table.
 */
export enum LocationCategory {
  EmployeeResident = 'employee_resident',
  MerkosOffice = 'merkos_office',
  Other = 'other',
}

export const LocationCategoryLabel: Record<LocationCategory, string> = {
  [LocationCategory.EmployeeResident]: 'Employee Resident',
  [LocationCategory.MerkosOffice]: 'Merkos Office',
  [LocationCategory.Other]: 'Other',
};

// ── Merkos Calendar (MT-1257) ──────────────────────────────────

/**
 * Type/category of an entry shown on the Merkos Calendar. Also the values a
 * user can filter the calendar by. Mirrors the `calendar_event_type` Postgres
 * enum.
 */
export enum CalendarEventType {
  Event = 'event',
  UsHoliday = 'us_holiday',
  JewishHoliday = 'jewish_holiday',
  Birthday = 'birthday',
  Personal = 'personal',
  Chabad = 'chabad',
  /** Employee out-of-office periods (derived from the user page). Off by default. */
  OutOfOffice = 'out_of_office',
}

export const CALENDAR_EVENT_TYPE_LABELS: Record<CalendarEventType, string> = {
  [CalendarEventType.Event]: 'Event',
  [CalendarEventType.UsHoliday]: 'US Holiday',
  [CalendarEventType.JewishHoliday]: 'Jewish Holiday',
  [CalendarEventType.Birthday]: 'Birthday',
  [CalendarEventType.Personal]: 'Personal',
  [CalendarEventType.Chabad]: 'Chabad',
  [CalendarEventType.OutOfOffice]: 'OOO',
};

/** Visibility of a manually-added calendar event. Mirrors `calendar_event_scope`. */
export enum CalendarEventScope {
  /** Authored by an admin; visible to everyone. */
  Global = 'global',
  /** Authored by a user for themselves; visible only to that user. */
  Personal = 'personal',
}

/** Whether a manual calendar event is anchored to a Gregorian or Hebrew date. Mirrors `calendar_date_type`. */
export enum CalendarDateType {
  Gregorian = 'gregorian',
  Hebrew = 'hebrew',
}

/** Which calendar-month system the monthly grid is laid out by. */
export enum CalendarViewMode {
  Gregorian = 'gregorian',
  Hebrew = 'hebrew',
}

// ── Fundraising (MT-1199) ──────────────────────────────────────

/** Status of a fundraising target plan assigned to an employee */
export enum TargetPlanStatus {
  Draft = 'draft',
  Active = 'active',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

export const TargetPlanStatusLabel: Record<TargetPlanStatus, string> = {
  [TargetPlanStatus.Draft]: 'Draft',
  [TargetPlanStatus.Active]: 'Active',
  [TargetPlanStatus.Completed]: 'Completed',
  [TargetPlanStatus.Cancelled]: 'Cancelled',
};

/** Status of a fundraising lead */
export enum LeadStatus {
  New = 'new',
  Contacted = 'contacted',
  InProgress = 'in_progress',
  Won = 'won',
  Lost = 'lost',
}

export const LeadStatusLabel: Record<LeadStatus, string> = {
  [LeadStatus.New]: 'New',
  [LeadStatus.Contacted]: 'Contacted',
  [LeadStatus.InProgress]: 'In Progress',
  [LeadStatus.Won]: 'Won',
  [LeadStatus.Lost]: 'Lost',
};

/** Status of a note/todo attached to a lead */
export enum LeadNoteStatus {
  Open = 'open',
  Done = 'done',
}

export const LeadNoteStatusLabel: Record<LeadNoteStatus, string> = {
  [LeadNoteStatus.Open]: 'Open',
  [LeadNoteStatus.Done]: 'Done',
};

/** Status of a pledge */
export enum PledgeStatus {
  Pending = 'pending',
  Confirmed = 'confirmed',
  PartiallyReceived = 'partially_received',
  Received = 'received',
  Cancelled = 'cancelled',
}

export const PledgeStatusLabel: Record<PledgeStatus, string> = {
  [PledgeStatus.Pending]: 'Pending',
  [PledgeStatus.Confirmed]: 'Confirmed',
  [PledgeStatus.PartiallyReceived]: 'Partially Received',
  [PledgeStatus.Received]: 'Received',
  [PledgeStatus.Cancelled]: 'Cancelled',
};

/** Payment methods for pledge distributions and received records */
export enum PledgePaymentMethod {
  Check = 'check',
  Transfer = 'transfer',
  CreditCard = 'credit_card',
  Cash = 'cash',
  Other = 'other',
}

export const PledgePaymentMethodLabel: Record<PledgePaymentMethod, string> = {
  [PledgePaymentMethod.Check]: 'Check',
  [PledgePaymentMethod.Transfer]: 'Bank Transfer',
  [PledgePaymentMethod.CreditCard]: 'Credit Card',
  [PledgePaymentMethod.Cash]: 'Cash',
  [PledgePaymentMethod.Other]: 'Other',
};

/**
 * Why `POST /time-entries/:id/stop` refused — the `code` of its 409 answer
 * (the time-rule 409s carry `blocked`/`requiresAcknowledgement` instead).
 */
export enum TimerStopRefusal {
  /** The entry was already closed — usually from another tab or device. The answer carries the entry. */
  AlreadyStopped = 'already_stopped',
}

export enum TimeEntryStatus {
  Draft = 'draft',
  Submitted = 'submitted',
  Approved = 'approved',
  Rejected = 'rejected',
  Paid = 'paid',
}

export const TimeEntryStatusLabel: Record<TimeEntryStatus, string> = {
  [TimeEntryStatus.Draft]: 'Draft',
  [TimeEntryStatus.Submitted]: 'Submitted',
  [TimeEntryStatus.Approved]: 'Approved',
  [TimeEntryStatus.Rejected]: 'Rejected',
  [TimeEntryStatus.Paid]: 'Paid',
};

// ── Time-entry alert rules (dynamic) ───────────────────────────
// Mirrors the CHECK constraints on time_alert_rules / time_entry_alerts
// (migrations 180 & 181). The DB is the source of truth — keep these in sync.

/**
 * The built-in library of measurable quantities a rule can be defined over.
 * Adding a brand-new metric kind requires extending the backend evaluator
 * (alert-evaluator.ts); adding rule *instances* over these is pure config.
 */
export enum TimeAlertMetric {
  /** Hours logged in the single time entry being closed. */
  SingleEntryHours = 'single_entry_hours',
  /** Total hours the employee has logged on the entry's local calendar day. */
  DailyHours = 'daily_hours',
  /** Total hours the employee has logged in the entry's local week (Mon start). */
  WeeklyHours = 'weekly_hours',
  /** Weekly hours as a percentage of the employee's expected weekly hours. */
  WeeklyPctExpected = 'weekly_pct_expected',
  /** Boolean: the entry's start and end fall on different local calendar days. */
  EntryCrossesDays = 'entry_crosses_days',
}

export const TimeAlertMetricLabel: Record<TimeAlertMetric, string> = {
  [TimeAlertMetric.SingleEntryHours]: 'Hours in a single entry',
  [TimeAlertMetric.DailyHours]: 'Hours in a single day',
  [TimeAlertMetric.WeeklyHours]: 'Hours in a week',
  [TimeAlertMetric.WeeklyPctExpected]: '% of expected weekly hours',
  [TimeAlertMetric.EntryCrossesDays]: 'Entry crosses days',
};

/** Metrics that compare against a numeric threshold (all except boolean ones). */
export const TIME_ALERT_THRESHOLD_METRICS: readonly TimeAlertMetric[] = [
  TimeAlertMetric.SingleEntryHours,
  TimeAlertMetric.DailyHours,
  TimeAlertMetric.WeeklyHours,
  TimeAlertMetric.WeeklyPctExpected,
];

/** Whether the metric requires a numeric threshold (false for boolean metrics). */
export function timeAlertMetricUsesThreshold(metric: TimeAlertMetric): boolean {
  return TIME_ALERT_THRESHOLD_METRICS.includes(metric);
}

/** How strongly a tripped rule is enforced. */
export enum TimeAlertEnforcement {
  /** Warn the user; they may acknowledge (dismiss) and proceed. Recorded. */
  Alert = 'alert',
  /** Prevent the action entirely. The blocked attempt is recorded. */
  Block = 'block',
}

export const TimeAlertEnforcementLabel: Record<TimeAlertEnforcement, string> = {
  [TimeAlertEnforcement.Alert]: 'Alert only',
  [TimeAlertEnforcement.Block]: 'Block',
};

/** The user action that causes rules to be evaluated. */
export enum TimeAlertTrigger {
  /** A time entry is closed — an end time is set (stop, or create/edit with an end). */
  EntryClose = 'entry_close',
  /** A manager submits time entries for approval. */
  ManagerSubmit = 'manager_submit',
}

export const TimeAlertTriggerLabel: Record<TimeAlertTrigger, string> = {
  [TimeAlertTrigger.EntryClose]: 'On entering time (closing an entry)',
  [TimeAlertTrigger.ManagerSubmit]: 'On manager submit',
};

/** Comparison operator applied between the measured metric and the threshold. */
export enum TimeAlertOperator {
  GreaterThan = 'gt',
  GreaterOrEqual = 'gte',
  LessThan = 'lt',
  LessOrEqual = 'lte',
}

export const TimeAlertOperatorLabel: Record<TimeAlertOperator, string> = {
  [TimeAlertOperator.GreaterThan]: 'more than',
  [TimeAlertOperator.GreaterOrEqual]: 'at least',
  [TimeAlertOperator.LessThan]: 'less than',
  [TimeAlertOperator.LessOrEqual]: 'at most',
};

// ── Subscriptions ──────────────────────────────────────────────
// Mirrors subscriptions table CHECK constraints (migration 035, 042, 044).

/** subscriptions.status */
export enum SubscriptionStatus {
  Request = 'request',
  Approved = 'approved',
  Active = 'active',
  Inactive = 'inactive',
}

export const SubscriptionStatusLabel: Record<SubscriptionStatus, string> = {
  [SubscriptionStatus.Request]: 'Request',
  [SubscriptionStatus.Approved]: 'Approved',
  [SubscriptionStatus.Active]: 'Active',
  [SubscriptionStatus.Inactive]: 'Inactive',
};

/** subscriptions.cost_type */
export enum SubscriptionCostType {
  Fixed = 'fixed',
  Estimate = 'estimate',
}

/** subscriptions.recurring_type */
export enum SubscriptionRecurringType {
  Monthly = 'monthly',
  Quarterly = 'quarterly',
  Yearly = 'yearly',
}

export const SubscriptionRecurringTypeLabel: Record<SubscriptionRecurringType, string> = {
  [SubscriptionRecurringType.Monthly]: 'Monthly',
  [SubscriptionRecurringType.Quarterly]: 'Quarterly',
  [SubscriptionRecurringType.Yearly]: 'Yearly',
};

// ── Payroll ────────────────────────────────────────────────────
// Mirrors payrolls.status CHECK constraint (migration 121).

export enum PayrollStatus {
  Draft = 'draft',
  Submitted = 'submitted',
  Closed = 'closed',
  Cancelled = 'cancelled',
}

export const PayrollStatusLabel: Record<PayrollStatus, string> = {
  [PayrollStatus.Draft]: 'Draft',
  [PayrollStatus.Submitted]: 'Submitted',
  [PayrollStatus.Closed]: 'Closed',
  [PayrollStatus.Cancelled]: 'Cancelled',
};

/**
 * Payrolls that are still "open" for linking a Payroll-method expense (MT-1426):
 * anything not yet Closed or Cancelled. Approved expenses linked to a payroll are
 * marked paid when it closes, so an expense can only attach while the payroll is
 * in one of these states.
 */
export const PAYROLL_OPEN_STATUSES: readonly PayrollStatus[] = [
  PayrollStatus.Draft,
  PayrollStatus.Submitted,
];

/**
 * Allowed payroll status transitions — the single source of truth shared by the
 * payroll list and detail workflow UIs. A Closed payroll is terminal: its linked
 * expenses are already marked paid (MT-1426), so it can no longer be cancelled.
 */
export const PAYROLL_STATUS_TRANSITIONS: Record<PayrollStatus, PayrollStatus[]> = {
  [PayrollStatus.Draft]: [PayrollStatus.Submitted, PayrollStatus.Cancelled],
  [PayrollStatus.Submitted]: [PayrollStatus.Closed, PayrollStatus.Draft, PayrollStatus.Cancelled],
  [PayrollStatus.Closed]: [],
  [PayrollStatus.Cancelled]: [],
};

/**
 * The kind of payroll a period belongs to. Mirrors the `payrolls.payroll_type`
 * CHECK constraint (migration 232). A payroll only ever collects time entries
 * for employees whose effective agreement maps to its type (see
 * {@link payrollTypeForEmployeeType}). `Regular` is the historical single
 * payroll (full-time/hourly, bi-weekly by default); `Contractor` is the
 * separate contractor run (monthly by default) whose accounting flow can also
 * generate per-contractor PDF invoices.
 */
export enum PayrollType {
  Regular = 'regular',
  Contractor = 'contractor',
}

export const PayrollTypeLabel: Record<PayrollType, string> = {
  [PayrollType.Regular]: 'Regular',
  [PayrollType.Contractor]: 'Contractor',
};

/**
 * How a payroll type's default period length is derived when creating a new
 * payroll. Stored per type in `admin_settings` (`PAYROLL_<TYPE>_CYCLE`), so the
 * cycle is admin-configurable rather than hardcoded. `Monthly` means a calendar
 * month (1st → last day); the fixed cycles below are a whole number of days;
 * `Custom` uses the companion `PAYROLL_<TYPE>_CYCLE_DAYS` day count.
 */
export enum PayrollCycle {
  Weekly = 'weekly',
  BiWeekly = 'biweekly',
  Monthly = 'monthly',
  Custom = 'custom',
}

export const PayrollCycleLabel: Record<PayrollCycle, string> = {
  [PayrollCycle.Weekly]: 'Weekly',
  [PayrollCycle.BiWeekly]: 'Bi-Weekly',
  [PayrollCycle.Monthly]: 'Monthly',
  [PayrollCycle.Custom]: 'Custom',
};

/**
 * Inclusive day span of a fixed (non-monthly, non-custom) cycle. Used to default
 * a new payroll's end date from its start date. `Monthly`/`Custom` are resolved
 * separately (calendar month, or the configured custom day count).
 */
export const PAYROLL_CYCLE_FIXED_DAYS: Partial<Record<PayrollCycle, number>> = {
  [PayrollCycle.Weekly]: 7,
  [PayrollCycle.BiWeekly]: 14,
};

/**
 * Maps an employee's contract `employee_type` to the payroll type their approved
 * time entries route into. Only `Contractor` employees route to the contractor
 * payroll; every other type (full-time, part-time, temporary, intern) and any
 * missing/unknown agreement fall back to the regular payroll. This is the single
 * source of truth for routing — used both when stamping a new time entry and
 * when picking which payrolls a contractor invoice can be generated for.
 */
export function payrollTypeForEmployeeType(employeeType: EmployeeType | string | null | undefined): PayrollType {
  return employeeType === EmployeeType.Contractor ? PayrollType.Contractor : PayrollType.Regular;
}

// ── Threads ────────────────────────────────────────────────────
// Mirrors threads.status (migration 030, 160) and inquiry_type (migration 051).

export enum ThreadStatus {
  Open = 'open',
  Resolved = 'resolved',
  Closed = 'closed',
  Backlog = 'backlog',
}

export const ThreadStatusLabel: Record<ThreadStatus, string> = {
  [ThreadStatus.Open]: 'Open',
  [ThreadStatus.Resolved]: 'Resolved',
  [ThreadStatus.Closed]: 'Closed',
  [ThreadStatus.Backlog]: 'Backlog',
};

export enum ThreadInquiryType {
  General = 'general',
  BugReport = 'bug_report',
  FeatureRequest = 'feature_request',
}

/**
 * How a thread message entered the system. Mirrors the `thread_messages.source`
 * CHECK constraint (migration 209). `InApp` is the default (typed in the app UI);
 * `Email` is a reply received by email (parsed by the inbound-reply Lambda from a
 * signed `reply+<token>@…` address). Drives the "via email" badge in ThreadPanel.
 */
export enum ThreadMessageSource {
  InApp = 'in_app',
  Email = 'email',
}

// ── Credit Cards ───────────────────────────────────────────────
// Mirrors credit_cards / cc_transactions CHECK constraints (migration 076).

export enum CardType {
  Amex = 'amex',
  Visa = 'visa',
  Mastercard = 'mastercard',
  Discover = 'discover',
  Other = 'other',
}

export enum CardStatus {
  Active = 'active',
  Inactive = 'inactive',
}

export enum CcTransactionStatus {
  Unmatched = 'unmatched',
  Matched = 'matched',
  Disputed = 'disputed',
}

/**
 * Bulk "create expenses from card charges" (no DB column): where a planned
 * expense's suggested vendor came from.
 */
export enum CcBulkVendorSource {
  /** A vendor reviewers confirmed for this merchant before (merchant alias). */
  Learned = 'learned',
  /** An active vendor whose name matches the merchant. */
  Name = 'name',
  /** No vendor found — the merchant name is used as an unlinked vendor name. */
  None = 'none',
}

export const CcBulkVendorSourceLabel: Record<CcBulkVendorSource, string> = {
  [CcBulkVendorSource.Learned]: 'Learned from past matches',
  [CcBulkVendorSource.Name]: 'Matched by name',
  [CcBulkVendorSource.None]: 'New vendor name',
};

/** Why a charge on the screen was left out of a bulk expense plan (no DB column). */
export enum CcBulkSkipReason {
  /** The charge already has a link (pending or confirmed) to an expense. */
  Linked = 'linked',
  /** The charge is matched or disputed, not unmatched. */
  NotUnmatched = 'not_unmatched',
  /** The charge's card belongs to another department (or none). */
  OtherDepartment = 'other_department',
}

export const CcBulkSkipReasonLabel: Record<CcBulkSkipReason, string> = {
  [CcBulkSkipReason.Linked]: 'Already linked to an expense',
  [CcBulkSkipReason.NotUnmatched]: 'Not unmatched (matched or disputed)',
  [CcBulkSkipReason.OtherDepartment]: "The card is not in the selected department",
};

/**
 * cc_transaction_expenses.match_source (migration 258) — who proposed the link.
 * Lets the review UI label a proposal and the matcher measure rule vs AI precision.
 */
export enum CcMatchSource {
  Rule = 'rule',
  Ai = 'ai',
  Manual = 'manual',
}

export const CcMatchSourceLabel: Record<CcMatchSource, string> = {
  [CcMatchSource.Rule]: 'Rule',
  [CcMatchSource.Ai]: 'AI',
  [CcMatchSource.Manual]: 'Manual',
};

// ── Vendors / Expenses target_account ───────────────────────────────────

/** vendors.vendor_type (migration 147) */
export enum VendorType {
  Individual = 'individual',
  SoleProprietor = 'sole_proprietor',
  Incorporated = 'incorporated',
  NonProfit = 'non_profit',
}

/** expenses.target_account_type (migration 108) */
export enum TargetAccountType {
  Vendor = 'vendor',
  Employee = 'employee',
}

export const TargetAccountTypeLabel: Record<TargetAccountType, string> = {
  [TargetAccountType.Vendor]: 'Vendor',
  [TargetAccountType.Employee]: 'Employee',
};

/**
 * vendors.expense_scope (migration 169) — controls which target_account a portal-login
 * vendor may select when creating an expense.
 *   self      → expenses are always recorded against the vendor themselves
 *   any_vendor → the vendor may pick any vendor from the list
 * In either case a vendor may never pay an employee.
 */
export enum VendorExpenseScope {
  SelfOnly = 'self',
  AnyVendor = 'any_vendor',
}

export const VENDOR_EXPENSE_SCOPE_LABELS: Record<VendorExpenseScope, string> = {
  [VendorExpenseScope.SelfOnly]: 'Only themselves',
  [VendorExpenseScope.AnyVendor]: 'Any vendor from the list',
};

// ── Remittances (ACH-01, migration 241) ────────────────────────

/** Rail used to send a remittance. Mirrors target_accounts.method / remittances.method. */
export enum RemittanceMethod {
  Ach = 'ach',
  Wire = 'wire',
}

export const RemittanceMethodLabel: Record<RemittanceMethod, string> = {
  [RemittanceMethod.Ach]: 'ACH',
  [RemittanceMethod.Wire]: 'Wire',
};

/** target_accounts.status */
export enum TargetAccountStatus {
  Pending = 'pending',
  Active = 'active',
  Failed = 'failed',
  Removed = 'removed',
}

export const TargetAccountStatusLabel: Record<TargetAccountStatus, string> = {
  [TargetAccountStatus.Pending]: 'Pending',
  [TargetAccountStatus.Active]: 'Active',
  [TargetAccountStatus.Failed]: 'Failed',
  [TargetAccountStatus.Removed]: 'Removed',
};

/** target_accounts.electronic_account_type */
export enum TargetAccountBankType {
  Checking = 'checking',
  Savings = 'savings',
}

export const TargetAccountBankTypeLabel: Record<TargetAccountBankType, string> = {
  [TargetAccountBankType.Checking]: 'Checking',
  [TargetAccountBankType.Savings]: 'Savings',
};

/** remittances.status — failed = no Mercury ack; returned = post-ack rail failure. */
export enum RemittanceStatus {
  Queued = 'queued',
  Initiated = 'initiated',
  Paid = 'paid',
  Failed = 'failed',
  Returned = 'returned',
}

export const RemittanceStatusLabel: Record<RemittanceStatus, string> = {
  [RemittanceStatus.Queued]: 'Queued',
  [RemittanceStatus.Initiated]: 'Initiated',
  [RemittanceStatus.Paid]: 'Paid',
  [RemittanceStatus.Failed]: 'Failed',
  [RemittanceStatus.Returned]: 'Returned',
};

/** remittance_batches.status — technical submit, not rail settlement. */
export enum RemittanceBatchStatus {
  Draft = 'draft',
  Processing = 'processing',
  Completed = 'completed',
  CompletedWithErrors = 'completed_with_errors',
  Failed = 'failed',
  Cancelled = 'cancelled',
}

export const RemittanceBatchStatusLabel: Record<RemittanceBatchStatus, string> = {
  [RemittanceBatchStatus.Draft]: 'Draft',
  [RemittanceBatchStatus.Processing]: 'Processing',
  [RemittanceBatchStatus.Completed]: 'Completed',
  [RemittanceBatchStatus.CompletedWithErrors]: 'Completed with errors',
  [RemittanceBatchStatus.Failed]: 'Failed',
  [RemittanceBatchStatus.Cancelled]: 'Cancelled',
};

// ── Employee Agreements ────────────────────────────────────────
// Mirrors employee_agreements CHECK constraints (migration 022).

export enum EmployeeAgreementType {
  Fixed = 'fixed',
  Hourly = 'hourly',
}

export const EmployeeAgreementTypeLabel: Record<EmployeeAgreementType, string> = {
  [EmployeeAgreementType.Fixed]: 'Fixed',
  [EmployeeAgreementType.Hourly]: 'Hourly',
};

export enum EmployeeAgreementPeriod {
  Yearly = 'yearly',
  Monthly = 'monthly',
}

export const EmployeeAgreementPeriodLabel: Record<EmployeeAgreementPeriod, string> = {
  [EmployeeAgreementPeriod.Yearly]: 'Yearly',
  [EmployeeAgreementPeriod.Monthly]: 'Monthly',
};

// ── Expense Import ─────────────────────────────────────────────
// Mirrors expense_import_runs.status (migration 087) and
// expense import exception status (migration 085).

export enum ImportRunStatus {
  Running = 'running',
  Completed = 'completed',
  Failed = 'failed',
}

export enum ImportExceptionStatus {
  Pending = 'pending',
  Resolved = 'resolved',
  Skipped = 'skipped',
}

// ── Notification Templates ─────────────────────────────────────
// Mirrors the notification_channel Postgres enum (migration 202).
// A notification template is stored per (template_key, channel):
// the in-app row (bell/mobile, plain text) and the email (HTML).

export enum NotificationChannel {
  InApp = 'in_app',
  Email = 'email',
}

// ── Entity-Linked File System (folders + albums) ───────────────
// Mirrors the `folders.kind` CHECK constraint (migration 212_file_system.sql).

/**
 * Kind of a *stored* folder row in the `folders` table. Virtual entity nodes
 * (departments, project groups, projects, vendors, the per-project expenses
 * aggregation) are NOT rows and are addressed by composite nodeId instead —
 * see FileNodeType. Only these three kinds are persisted:
 *   - System — a seeded top-level root (General / HR / Accounting), not deletable.
 *   - Custom — a user-created folder hung off any node at any level.
 *   - Album  — a user-created folder rendered as a media gallery; its files carry
 *              richer {@link FileMetadata}.
 */
export enum FolderKind {
  System = 'system',
  Custom = 'custom',
  Album = 'album',
}

/**
 * Media classification derived from a file's content-type at upload, stored in
 * `documents.media_kind`. Drives gallery rendering and which metadata fields are
 * relevant (dimensions for images, duration for videos).
 */
export enum FileMediaKind {
  File = 'file',
  Image = 'image',
  Video = 'video',
}

/**
 * The seeded top-level system roots (folders with kind=System). `system_key`
 * mirrors this exactly. General is visible to every authenticated user; HR and
 * Accounting are visible only to global roles (see GLOBAL_ROLES).
 */
export enum SystemFolderKey {
  General = 'general',
  HR = 'hr',
  Accounting = 'accounting',
}

export const SystemFolderKeyLabel: Record<SystemFolderKey, string> = {
  [SystemFolderKey.General]: 'General',
  [SystemFolderKey.HR]: 'HR',
  [SystemFolderKey.Accounting]: 'Accounting',
};

/**
 * The kinds of node the file-explorer tree exposes. Persisted folders resolve to
 * one of System/Custom/Album (via {@link FolderKind}); the rest are virtual
 * nodes derived live from domain entities and addressed by composite nodeId
 * (e.g. `project:<uuid>`). Not a DB column — a transport/UI discriminator.
 */
export enum FileNodeType {
  /** The synthetic top-level container ("All Files") custom folders hang off. */
  Root = 'root',
  SystemRoot = 'system_root',
  VendorsRoot = 'vendors_root',
  Department = 'department',
  ProjectGroup = 'project_group',
  Project = 'project',
  Vendor = 'vendor',
  /** A single employee's folder (virtual, under the HR root and on their page). */
  Employee = 'employee',
  /** The per-project aggregation of its expenses' documents (read-only). */
  ProjectExpenses = 'project_expenses',
  /**
   * The "Payroll" root (virtual, under the Accounting root). Parents one folder
   * per payroll that has generated contractor invoices. Read-only, global-only.
   */
  PayrollRoot = 'payroll_root',
  /** A single payroll's folder of generated contractor invoices (read-only). */
  Payroll = 'payroll',
  /**
   * The "Invoices" folder under a single employee's folder — the contractor
   * invoices generated for that employee across payrolls (read-only).
   */
  EmployeeInvoices = 'employee_invoices',
  Custom = 'custom',
  Album = 'album',
  /**
   * The admin-only "Backup" root and every folder beneath it. Backup nodes are a
   * virtual, read-only view over the raw S3 backup bucket (not `folders` rows), so
   * they carry no entity and permit no writes.
   */
  Backup = 'backup',
}

// ── Backup & Recovery (MT-1309) ─────────────────────────────────────────────
// These enums mirror the CHECK constraints on the backup_runs table
// (migration 213_backup_recovery.sql) exactly.

/**
 * Hard maximum retention (days) the configurable BACKUP_RETENTION_DAYS setting is
 * allowed to reach. The backup bucket's S3 lifecycle rule expires objects at this
 * age regardless of the runtime setting, so accepting/reporting a larger value
 * would be misleading. MUST stay in sync with MAX_RETENTION_DAYS in
 * infra/lib/backup-stack.ts.
 */
export const BACKUP_MAX_RETENTION_DAYS = 365;

/** Which persistence store a backup run covers (backup_runs.store_type). */
export enum BackupStoreType {
  Db = 'db',
  S3 = 's3',
}

export const BackupStoreTypeLabel: Record<BackupStoreType, string> = {
  [BackupStoreType.Db]: 'Database',
  [BackupStoreType.S3]: 'S3 Files',
};

/** Lifecycle state of a backup run (backup_runs.status). */
export enum BackupRunStatus {
  Running = 'running',
  Success = 'success',
  Error = 'error',
}

export const BackupRunStatusLabel: Record<BackupRunStatus, string> = {
  [BackupRunStatus.Running]: 'Running',
  [BackupRunStatus.Success]: 'Success',
  [BackupRunStatus.Error]: 'Error',
};

/** How a backup run was initiated (backup_runs.trigger). */
export enum BackupTrigger {
  Scheduled = 'scheduled',
  Manual = 'manual',
}

export const BackupTriggerLabel: Record<BackupTrigger, string> = {
  [BackupTrigger.Scheduled]: 'Scheduled',
  [BackupTrigger.Manual]: 'Manual',
};

/** Lifecycle state of a DB restore (backup_restores.status, migration 214). */
export enum BackupRestoreStatus {
  Running = 'running',
  Success = 'success',
  Error = 'error',
}

export const BackupRestoreStatusLabel: Record<BackupRestoreStatus, string> = {
  [BackupRestoreStatus.Running]: 'Running',
  [BackupRestoreStatus.Success]: 'Success',
  [BackupRestoreStatus.Error]: 'Error',
};

// ---------------------------------------------------------------------------
// Office / desk management (migration 241)
// ---------------------------------------------------------------------------

/** Room classification (rooms.room_type). Mirrors the `room_type` Postgres enum. */
export enum RoomType {
  General = 'general',
  Private = 'private',
  Conference = 'conference',
  Studio = 'studio',
  Kitchen = 'kitchen',
  Storage = 'storage',
  Other = 'other',
}

export const RoomTypeLabel: Record<RoomType, string> = {
  [RoomType.General]: 'General',
  [RoomType.Private]: 'Private',
  [RoomType.Conference]: 'Conference',
  [RoomType.Studio]: 'Studio',
  [RoomType.Kitchen]: 'Kitchen',
  [RoomType.Storage]: 'Storage',
  [RoomType.Other]: 'Other',
};

/** Operational state of a desk (desks.status). Mirrors the `desk_status` Postgres enum. */
export enum DeskStatus {
  Active = 'active',
  Inactive = 'inactive',
  Maintenance = 'maintenance',
}

export const DeskStatusLabel: Record<DeskStatus, string> = {
  [DeskStatus.Active]: 'Active',
  [DeskStatus.Inactive]: 'Inactive',
  [DeskStatus.Maintenance]: 'Maintenance',
};

/** How a desk assignment is bounded in time (desk_assignments.assignment_type). */
export enum DeskAssignmentType {
  Permanent = 'permanent',
  Temporary = 'temporary',
}

export const DeskAssignmentTypeLabel: Record<DeskAssignmentType, string> = {
  [DeskAssignmentType.Permanent]: 'Permanent',
  [DeskAssignmentType.Temporary]: 'Temporary',
};

/**
 * Weekday labels indexed by JS `getDay()` (0=Sunday .. 6=Saturday) — the same
 * convention used by desk_assignment_schedules.day_of_week and elsewhere.
 */
export const WEEKDAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'] as const;
export const WEEKDAY_LABELS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

/** Lifecycle of a desk booking request (desk_bookings.status, migration 243). */
export enum BookingStatus {
  Requested = 'requested',
  Approved = 'approved',
  Rejected = 'rejected',
  Cancelled = 'cancelled',
}

export const BookingStatusLabel: Record<BookingStatus, string> = {
  [BookingStatus.Requested]: 'Requested',
  [BookingStatus.Approved]: 'Approved',
  [BookingStatus.Rejected]: 'Rejected',
  [BookingStatus.Cancelled]: 'Cancelled',
};

/** Where the budget-comparison report reads prior fiscal years' actuals from.
 *  Request-only value (no DB column): `system` = expenses + salaries + income
 *  recorded in this app; `quickbooks` = QuickBooks transactions (Athena) by QB class. */
export enum BudgetComparisonSource {
  System = 'system',
  QuickBooks = 'quickbooks',
}

/** What a budget-comparison breakdown row represents. Request-only (no DB column). */
export enum BudgetComparisonRowKind {
  /** A single project. */
  Project = 'project',
  /** A department-budget line set at project-group level (no single project). */
  ProjectGroup = 'project_group',
  /** A QuickBooks class shared by several projects (or owned by the group/department). */
  QbClass = 'qb_class',
  /** Department-level activity not attributable to any project (or, in the
   *  organization report, QuickBooks activity not linked to any department). */
  Unallocated = 'unallocated',
  /** A whole department (organization-budget comparison). */
  Department = 'department',
}

/**
 * Value type of an admin setting (`admin_settings.variable_type`, VARCHAR — validated
 * by the admin-config handlers). `Users` stores a JSON array of user ids.
 */
export enum AdminSettingVariableType {
  Text = 'text',
  Number = 'number',
  Date = 'date',
  Boolean = 'boolean',
  User = 'user',
  Users = 'users',
  List = 'list',
  Account = 'account',
}

/** Admin setting codes read by more than one package. */
export const VENDOR_MESSAGE_CONTACTS_SETTING = 'VENDOR_MESSAGE_CONTACTS';

/**
 * Expiry state of an employee's latest agreement, surfaced as a top banner
 * (MT-1654). Computed, not stored — no DB enum to mirror.
 */
export enum AgreementExpiryState {
  Expiring = 'expiring',
  Expired = 'expired',
}

/** FEATURE_GATE list `var_code` that turns on the agreement expiry banner (MT-1654). */
export const AGREEMENT_EXPIRY_BANNER_GATE = 'AGREEMENT_EXPIRY_BANNER';

/** Fallback for the AGREEMENT_EXPIRY_WARNING_DAYS admin setting. */
export const DEFAULT_AGREEMENT_EXPIRY_WARNING_DAYS = 10;

// ── Custom dashboards (MT-1666) ───────────────────────────

/** Mirrors the `dashboard_status` Postgres enum. Inactive suspends sharing + schedules. */
export enum DashboardStatus {
  Active = 'active',
  Inactive = 'inactive',
}

/** Mirrors the `chk_dashboard_card_type` CHECK on `dashboard_cards.card_type`. */
export enum DashboardCardType {
  /** Legacy: latest value of a metric query */
  MetricValue = 'metric_value',
  /** Legacy: metric query value + sparkline */
  Sparkline = 'sparkline',
  Objective = 'objective',
  /** Static label + value */
  Stat = 'stat',
  /** A metric-entry series — the department page's metric card (graph + history) */
  Metric = 'metric',
  /** Free text heading */
  Title = 'title',
  /** Background frame that visually groups the elements placed on it */
  Frame = 'frame',
}

/** Mirrors the `chk_dashboard_access_level` CHECK — shares are view-only. */
export enum DashboardAccessLevel {
  View = 'view',
}

/** Mirrors the `dashboard_schedule_cadence` Postgres enum. */
export enum DashboardScheduleCadence {
  Daily = 'daily',
  Weekly = 'weekly',
  Monthly = 'monthly',
}
// ── SQL bridge (Admin → Monitor → SQL Bridge) ──────────────────
// Mirror the CHECK lists of `bridge_clients` / `bridge_access_log` (migration
// 264, from `@speles7172/sql-client`'s bridgeAccessSchemaSql). The package's
// tuples are the same values; `sql-bridge.test.ts` pins that they agree.

/** Whether a client app may use the SQL bridge. */
export enum SqlBridgeClientStatus {
  Active = 'active',
  Blocked = 'blocked',
}

/** What a client app may do through the SQL bridge. */
export enum SqlBridgeAccessMode {
  ReadOnly = 'read_only',
  ReadWrite = 'read_write',
}

/**
 * How a client app's table list reads (migration 267's CHECK): `allow` only the
 * listed tables and columns, or `block` them and read the rest.
 */
export enum SqlBridgeExposureMode {
  Allow = 'allow',
  Block = 'block',
}

/** How one call through the SQL bridge ended. */
export enum SqlBridgeAccessOutcome {
  Succeeded = 'succeeded',
  Failed = 'failed',
  Denied = 'denied',
}

/** Why the SQL bridge refused a call. */
export enum SqlBridgeDenialReason {
  MissingCredentials = 'missing_credentials',
  UnknownClient = 'unknown_client',
  InvalidKey = 'invalid_key',
  Blocked = 'blocked',
  TargetNotAllowed = 'target_not_allowed',
  WriteNotAllowed = 'write_not_allowed',
  InvalidRequest = 'invalid_request',
}

/** The bridge name management's SQL bridge registers its clients under. */
export const SQL_BRIDGE_NAME = 'sql';
/** The one target management's SQL bridge serves: its own database. */
export const SQL_BRIDGE_TARGET = 'management';

/**
 * The kinds of record the global search (`GET /search`) answers — one provider
 * each in `backend/src/handlers/search/providers.ts`, one chip each in the
 * search dialog. Values match `EntityType` where the record has one.
 */
export enum SearchResultType {
  Expense = 'expense',
  Thread = 'thread',
  Notification = 'notification',
  Document = 'document',
  Project = 'project',
  Department = 'department',
  Employee = 'employee',
  Vendor = 'vendor',
  TimeEntry = 'time_entry',
  Budget = 'budget',
  CcTransaction = 'cc_transaction',
  Dashboard = 'dashboard',
}

/** Who wrote a message in a How-to conversation (migration 273's CHECK on `howto_messages.role`). */
export enum HowtoMessageRole {
  User = 'user',
  Assistant = 'assistant',
}

/**
 * Where a How-to message stands (migration 273's CHECK on `howto_messages.status`):
 * a question is `done` when written; an answer is `pending` until the model replies.
 */
export enum HowtoMessageStatus {
  Pending = 'pending',
  Done = 'done',
  Failed = 'failed',
}

/** What kind of reference material an answer drew on (shown under the answer). */
export enum HowtoSourceKind {
  Doc = 'doc',
  Screen = 'screen',
  Code = 'code',
}
